<template>
  <div class="style-evaluation-step">
    <div class="style-evaluation-content">
      <!-- Title Section -->
      <div class="title-section">
        <h2 class="step-title">{{ step.title }}</h2>
        <p v-if="step.subtitle" class="step-subtitle">{{ step.subtitle }}</p>
      </div>

      <!-- Styles Grid -->
      <div class="styles-grid">
        <div
          v-for="style in step.selectedStyles"
          :key="style.styleId"
          class="style-card"
          :class="{ selected: isStyleSelected(style.styleId), completed: style.isCompleted }"
          @click="toggleStyle(style.styleId)"
        >
          <!-- Style Image -->
          <div class="style-image-wrapper">
            <img
              :src="getStyleImage(style)"
              :alt="style.name"
              class="style-image"
            />
            <div v-if="style.isCompleted" class="completed-badge">
              <i class="pi pi-check"></i>
            </div>
          </div>

          <!-- Style Info -->
          <div class="style-info">
            <div class="style-name">{{ style.name }}</div>
            <div class="style-progress">
              <div class="progress-bar">
                <div
                  class="progress-fill"
                  :style="{ width: `${style.progressPercent}%` }"
                ></div>
              </div>
              <div class="progress-text">
                {{ style.progressPercent }}% · {{ style.swipedCards }}/{{ step.swipesForAccuracy }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useTasteClarificationModal } from '@/features/taste/store'
import type { TasteStep } from '@/features/taste/types/taste-config'
import { mapStyleImageFromBackend } from '@/utils/style-image-mapper'

interface Props {
  step: TasteStep & { type: 'style_evaluation' }
}

const props = defineProps<Props>()
const tasteStore = useTasteClarificationModal()

const selectedStyleIds = computed(() => {
  const data = tasteStore.tasteModal.tasteData[props.step.id]
  if (!data) return []
  
  // Если это массив, возвращаем как есть (нормализуем к числам)
  if (Array.isArray(data)) {
    return data.map((id: any) => typeof id === 'number' ? id : parseInt(id, 10)).filter((id: any) => !isNaN(id))
  }
  
  // Если это одно значение (старый формат), преобразуем в массив
  if (typeof data === 'string' || typeof data === 'number') {
    const id = typeof data === 'number' ? data : parseInt(data, 10)
    return isNaN(id) ? [] : [id]
  }
  
  return []
})

function isStyleSelected(styleId: number): boolean {
  return selectedStyleIds.value.includes(styleId)
}

function toggleStyle(styleId: number | string) {
  const styleIdNumber = typeof styleId === 'number' ? styleId : parseInt(styleId, 10)
  if (isNaN(styleIdNumber)) return
  
  const currentIds = [...selectedStyleIds.value]
  const index = currentIds.indexOf(styleIdNumber)
  
  if (index > -1) {
    // Удаляем стиль из выбранных
    currentIds.splice(index, 1)
  } else {
    // Добавляем стиль к выбранным
    currentIds.push(styleIdNumber)
  }
  
  // Сохраняем обновленный список выбранных стилей
  tasteStore.updateTasteData(props.step.id, currentIds)
  
  // Сохраняем выбранные стили для использования в tinder_like шаге
  // Используем ключ 'style-evaluation-choice' для обратной совместимости
  tasteStore.updateTasteData('style-evaluation-choice', currentIds.map(id => id.toString()))
}

function getStyleImage(style: { code?: string; image?: string }): string {
  return mapStyleImageFromBackend(style)
}

// Автоматически выбираем стили из style-selection-step при монтировании
onMounted(() => {
  // Находим шаг style_selection в конфигурации
  const styleSelectionStep = tasteStore.steps.find(step => step.type === 'style_selection')
  
  if (!styleSelectionStep) return
  
  // Получаем выбранные стили из tasteData для шага style_selection
  const selectedStylesFromSelection = tasteStore.tasteModal.tasteData[styleSelectionStep.id]
  
  if (!selectedStylesFromSelection || !Array.isArray(selectedStylesFromSelection)) return
  
  // Нормализуем ID к числам
  const selectedStyleIdsFromSelection = selectedStylesFromSelection
    .map((id: any) => typeof id === 'number' ? id : parseInt(id, 10))
    .filter((id: any) => !isNaN(id))
  
  if (selectedStyleIdsFromSelection.length === 0) return
  
  // Находим стили из step.selectedStyles, которые соответствуют выбранным на style-selection-step
  const stylesToSelect = props.step.selectedStyles
    .filter(style => selectedStyleIdsFromSelection.includes(style.styleId))
    .map(style => style.styleId)
  
  if (stylesToSelect.length === 0) return
  
  // Проверяем, есть ли уже сохраненные данные для текущего шага
  const currentData = tasteStore.tasteModal.tasteData[props.step.id]
  
  // Если данных еще нет, автоматически выбираем все найденные стили
  if (!currentData || (Array.isArray(currentData) && currentData.length === 0)) {
    tasteStore.updateTasteData(props.step.id, stylesToSelect)
    tasteStore.updateTasteData('style-evaluation-choice', stylesToSelect.map(id => id.toString()))
  }
})
</script>

<style scoped>
.style-evaluation-step {
  display: flex;
  padding: var(--Padding-XXL, 40px) 120px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: var(--Spacing-XL, 40px);
  align-self: stretch;
}

.style-evaluation-content {
  max-width: 1200px;
  width: 100%;
}

.title-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 40px;
  text-align: center;
}

.step-title {
  font-family: 'Gilroy';
  font-size: 48px;
  font-weight: 700;
  color: var(--primary-blue-100);
  margin: 0;
  line-height: 1.2;
}

.step-subtitle {
  font-family: 'Inter';
  font-size: 20px;
  color: var(--primary-blue-80);
  line-height: 1.3;
  margin: 0;
}

.styles-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 24px;
  width: 100%;
}

.style-card {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  border-radius: 16px;
  background: white;
  border: 2px solid rgba(35, 47, 69, 0.1);
  cursor: pointer;
  transition: all 0.3s ease;
}

.style-card:hover {
  border-color: var(--primary-blue-100);
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(35, 47, 69, 0.1);
}

.style-card.selected {
  border-color: var(--primary-blue-100);
  background: rgba(110, 133, 203, 0.05);
}

.style-card.completed {
  opacity: 0.7;
}

.style-image-wrapper {
  position: relative;
  width: 100%;
  aspect-ratio: 3 / 4;
  border-radius: 12px;
  overflow: hidden;
  background: #f5f5f5;
}

.style-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.completed-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--primary-blue-100);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

.style-info {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.style-name {
  font-family: 'Gilroy';
  font-size: 20px;
  font-weight: 600;
  color: var(--primary-blue-100);
  line-height: 1.3;
}

.style-progress {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.progress-bar {
  width: 100%;
  height: 8px;
  border-radius: 4px;
  background: rgba(35, 47, 69, 0.1);
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: var(--primary-blue-100);
  transition: width 0.3s ease;
}

.progress-text {
  font-family: 'Inter';
  font-size: 14px;
  color: rgba(35, 47, 69, 0.6);
  line-height: 1.4;
}

@media (max-width: 1024px) {
  .style-evaluation-step {
    padding: 40px 20px;
  }

  .step-title {
    font-size: 32px;
  }

  .step-subtitle {
    font-size: 16px;
  }

  .styles-grid {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 16px;
  }
}

@media (max-width: 768px) {
  .styles-grid {
    grid-template-columns: 1fr;
  }
}
</style>

